import { Button } from '@components/Button'
import { HeroIcon } from '@components/HeroIcon'
import { HeroTooltip } from '@components/HeroTooltip'
import { IExtraContent } from '@dtos/Course'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { useEnrollment } from '@hooks/useEnrollment'
import { useGetMedia } from '@services/api/graphql/getMedia'
import { GestureReponderEvent, useMedia } from '@tamagui/core'
import { courseExtraContentAvailability } from '@utils/courseExtraContentAvailability'
import { LessonTypeTranslation } from '@utils/lessonMediaType'
import { ILessonExtraContentAvailability } from '@utils/lessonExtraContentAvailability'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardProps, isWeb, Text, XStack, YStack } from 'tamagui'
import { useCourseRouteParams } from '@contexts/CourseRouteParamsContext'

interface IExtraContentCardProps extends CardProps {
  extraContent: IExtraContent
  handlePressDownload: (
    e: GestureReponderEvent,
    link: string | null | undefined,
  ) => void
  handlePressOpenModal: () => void
  isLessonAvailable?: boolean
  availableText?: string
  // Já resolvida por quem chama (conteúdo adicional de aula); substitui o
  // cálculo pelos campos de liberação do extraContent.
  availability?: ILessonExtraContentAvailability
  withBorder?: boolean
  flexDirection?: 'row' | 'column'
  iconHeight?: number | string
  iconWidth?: number | string
  widthValue?: number | string
}

export function ExtraContentCard({
  extraContent,
  handlePressDownload,
  handlePressOpenModal,
  isLessonAvailable,
  availableText,
  availability,
  withBorder = false,
  flexDirection,
  iconHeight,
  iconWidth,
  widthValue,
  ...extraProps
}: IExtraContentCardProps) {
  const { courseId, enrollmentId } = useCourseRouteParams()
  const [isHoveringBtn, setIsHoveringBtn] = useState(false)
  const media = useMedia()
  const { t } = useTranslation('ExtraContent')
  const { enrollment } = useEnrollment()
  const notDownloadable =
    extraContent.media.type === 'Video' ||
    extraContent.media.type === 'Text' ||
    extraContent.media.fileUrl === null ||
    !extraContent.downloadable

  const [shouldFetch, setShouldFetch] = useState(false)
  const { data: mediaData, loading } = useGetMedia({
    courseId: courseId?.toString() ?? '0',
    enrollmentId: enrollmentId?.toString() ?? '0',
    id: extraContent?.media?.id?.toString() ?? '0',
    enabled: shouldFetch,
  })

  const displayTitleTooltip = extraContent.title.length > 80

  const cardWidth = useMemo(() => {
    if (media.gtMd) return 407
    if (media.gtSm) return 324
    return 242
  }, [media.gtMd, media.gtSm])

  const cardPadding = media.gtMd ? '$spacing-inset-sm' : '$spacing-inset-xs'
  const ownAvailability = courseExtraContentAvailability(
    extraContent,
    enrollment,
  )
  const isAvailable = availability
    ? availability.isAvailable
    : ownAvailability.isAvailable && isLessonAvailable === true
  const extraContentDateText = availability
    ? availability.text
    : isLessonAvailable === false
      ? availableText
      : ownAvailability.text

  const downloadButtonColor = notDownloadable
    ? '$text-disabled'
    : isHoveringBtn
      ? '$accent-hover'
      : '$accent'

  const cursorStyle = isAvailable ? 'pointer' : 'default'
  const cardFlexDirection = flexDirection || (media.gtSm ? 'row' : 'column')
  const isRowLayout = cardFlexDirection === 'row'
  const iconBgColor = isAvailable ? '$bg-surface-sunken' : '$bg-locked'
  const iconMediaColor = isAvailable ? '$accent' : '$text-on-locked'

  const iconContainerHeight = useMemo(() => {
    if (media.gtMd) return 142
    if (media.gtSm) return 116
    return 48
  }, [media.gtMd, media.gtSm])

  const iconName = useMemo((): IconProp => {
    if (!isAvailable) return ['fal', 'lock-keyhole']
    return LessonTypeTranslation(
      extraContent.media.type,
      extraContent.media?.fileExt?.toLowerCase() || null,
    )
  }, [isAvailable, extraContent.media.type, extraContent.media?.fileExt])

  const finalValue = widthValue ?? cardWidth
  const cardWidthStyle = isWeb
    ? typeof finalValue === 'string' && finalValue.includes('%')
      ? `${finalValue} !important`
      : `${finalValue}px !important`
    : finalValue

  const iconContainerWidth = useMemo(() => {
    if (media.gtMd) return 80
    if (media.gtSm) return 64
    return '100%'
  }, [media.gtMd, media.gtSm])

  const handleCardPress = useCallback(() => {
    if (!isAvailable) return null
    handlePressOpenModal()
  }, [isAvailable, handlePressOpenModal])

  const hoverStyleData = isAvailable
    ? {
        borderColor: withBorder ? '$border-muted' : '$border-hover',
        borderWidth: withBorder ? 1 : 2,
        backgroundColor: withBorder ? '$bg-surface-raised' : '$bg-surface',
      }
    : {}

  const titleFontSize = useMemo(() => {
    if (media.gtXl) return '$font-size-sm'
    if (media.gtSm) return '$font-size-xs'
    return '$font-size-xxs'
  }, [media.gtXl, media.gtSm])

  const handleDownload = useCallback(
    (url: string) => {
      try {
        const event = {
          stopPropagation: () => {
            if (isWeb) {
              window.event?.stopPropagation()
            }
          },
        } as GestureReponderEvent

        handlePressDownload(event, url)
      } catch (error) {
        console.error(error)
      } finally {
        setTimeout(() => {
          setShouldFetch(false)
        }, 100)
      }
    },
    [handlePressDownload],
  )

  useEffect(() => {
    if (mediaData?.media?.fileUrl && !loading && shouldFetch) {
      const currentFileUrl: string = mediaData.media.fileUrl

      if (currentFileUrl && typeof currentFileUrl === 'string') {
        handleDownload(currentFileUrl)
      }
    }
  }, [mediaData?.media?.fileUrl, handleDownload, loading, shouldFetch])

  return (
    <Card
      {...extraProps}
      backgroundColor="$bg-surface"
      padding={cardPadding}
      flexDirection={cardFlexDirection}
      borderRadius={12}
      borderColor={withBorder ? '$border-hover' : 'transparent'}
      borderWidth={withBorder ? 1 : 2}
      gap="$spacing-inset-xs"
      onPress={handleCardPress}
      {...(!isWeb && {
        pressStyle: isAvailable ? { opacity: 0.7, scale: 0.98 } : {},
        animation: 'quick',
      })}
      cursor={cursorStyle}
      minWidth={cardWidthStyle}
      maxWidth={cardWidthStyle}
      hoverStyle={hoverStyleData}
    >
      <XStack
        borderRadius={8}
        bg={iconBgColor}
        alignItems="center"
        justifyContent="center"
        // Em linha, o bloco acompanha a altura do card (que pode vir maior pelo
        // minHeight de quem chama), então o fim do texto alinha com o fim dele.
        {...(isRowLayout
          ? {
              minHeight: iconHeight || iconContainerHeight,
              alignSelf: 'stretch',
            }
          : { height: iconHeight || iconContainerHeight })}
        width={iconWidth || iconContainerWidth}
      >
        <HeroIcon icon={iconName} color={iconMediaColor} size={24} />
      </XStack>
      <YStack
        flex={1}
        gap="$spacing-inset-xxs"
        justifyContent="space-between"
        paddingVertical="$spacing-inset-xxxs"
      >
        <HeroTooltip
          text={extraContent.title}
          width={200}
          display={displayTitleTooltip}
        >
          <Text
            numberOfLines={3}
            color="$text-primary"
            ff="$mulishSemiBold"
            fontSize={titleFontSize}
          >
            {extraContent.title}
          </Text>
        </HeroTooltip>
        {!isAvailable && (
          <XStack gap={4} alignItems="center" testID="unavailable-content-date">
            <HeroIcon
              size={16}
              icon={['fal', 'calendar-alt']}
              color="$icon-primary"
            />
            <Text
              ff="$mulishRegular"
              fontSize="$font-size-xxs"
              color="$text-primary"
              position="relative"
            >
              {extraContentDateText}
            </Text>
          </XStack>
        )}
        {isAvailable && (
          <HeroTooltip
            text={t('ExtraContentCard.tooltip')}
            width={158}
            display={notDownloadable}
          >
            <Button
              testID="extra-content-download-button"
              variant="secondary"
              aria-label="download"
              outline
              onPress={(e) => {
                e.stopPropagation()
                if (!shouldFetch) {
                  setShouldFetch(true)
                }
              }}
              disabled={notDownloadable}
              onHoverIn={() => setIsHoveringBtn(true)}
              onHoverOut={() => setIsHoveringBtn(false)}
              w="100%"
              loading={shouldFetch}
            >
              <XStack
                gap={8}
                alignItems="center"
                justifyContent="center"
                w="100%"
              >
                <HeroIcon
                  size={16}
                  icon={['fal', 'download']}
                  color={downloadButtonColor}
                />
                <Text
                  ff="$mulishBold"
                  fontSize="$font-size-xxs"
                  color={downloadButtonColor}
                >
                  {shouldFetch
                    ? t('ExtraContentCard.downloading')
                    : t('ExtraContentCard.download')}
                </Text>
              </XStack>
            </Button>
          </HeroTooltip>
        )}
      </YStack>
    </Card>
  )
}
